import { useCrmLocale } from '../../ui/locale/crmLocale';
import CrmNavigationSidebar from '../crm/CrmNavigationSidebar';
import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  Calculator, ChartPie, Headset,
  HeartPulse, House, ListChecks, Megaphone, UsersRound, UserRoundCog,
} from 'lucide-react';
import { PrefetchNavLink } from '../PrefetchLink';
import { useAuth } from '../../auth/useAuth';

interface Props {
  collapsed?: boolean;
  mobile?: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
}

const ManagerSidebar: React.FC<Props> = ({ collapsed = false, onToggle, onNavigate }) => {
  const { t: crmText } = useCrmLocale();

  const location = useLocation();
  const { user } = useAuth();
  const isGeneralManager = String(user?.role || '').toLowerCase() === 'general_manager';
  const sections = [
    { title: 'Workspace', items: [
      { to: '/', icon: House, label: 'Dashboard', exact: true },
      { to: '/employees', icon: UsersRound, label: 'Employees' },
      { to: '/tasks', icon: ListChecks, label: 'Tasks' },
    ] },
    { title: 'Departments', items: [
      { to: '/app/reception', icon: Headset, label: 'Reception', external: true },
      ...(isGeneralManager ? [{ to: '/hr', icon: UserRoundCog, label: 'HR', external: true }] : []),
      { to: '/accountant', icon: Calculator, label: 'Accountant', external: true },
      { to: '/app/sales', icon: ChartPie, label: 'Sales', external: true },
      { to: '/marketing', icon: Megaphone, label: 'Marketing', external: true },
      { to: '/diagnostics/dashboard', icon: HeartPulse, label: 'System Health', external: true },
    ] },
  ];
  return <CrmNavigationSidebar label={crmText("Manager navigation")} collapsed={collapsed} onToggle={onToggle}>{compact => (
    <nav className="manager-sidebar-nav" aria-label={crmText("Manager navigation")}>
      {sections.map(section => <section className="manager-nav-section" key={section.title} aria-label={crmText(section.title)}>
        {compact ? <div className="manager-nav-divider" title={crmText(section.title)} /> : <h2>{crmText(section.title)}</h2>}
        {section.items.map(item => {
          const Icon = item.icon;
          const contents = <><Icon size={18} aria-hidden="true" />{!compact && <span>{crmText(item.label)}</span>}</>;
          return 'external' in item && item.external ? (
            <a key={item.to} href={item.to} className="manager-nav-link" aria-label={crmText(item.label)}
              title={compact ? crmText(item.label) : undefined} onClick={onNavigate}>{contents}</a>
          ) : (
            <PrefetchNavLink key={item.to} to={item.to} end={'exact' in item && item.exact}
              aria-label={crmText(item.label)} title={compact ? crmText(item.label) : undefined} onClick={onNavigate}
              className={({ isActive }) => {
                const active = item.to === '/'
                  ? ['/', '/manager', '/manager/'].includes(location.pathname) : isActive;
                return `manager-nav-link${active ? ' is-active' : ''}`;
              }}>{contents}</PrefetchNavLink>
          );
        })}
      </section>)}
    </nav>
  )}</CrmNavigationSidebar>;
};

export default ManagerSidebar;
